import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Shield, Mail, Clock, AlertCircle, CheckCircle } from "lucide-react";
const BASE_URL = process.env.REACT_APP_BASE_URL;
function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const [timeLeft, setTimeLeft] = useState(null);
  const [otp, setOtp] = useState(["", "", "", ""]); // Changed to 4 digits
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTime, setBlockTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  // Refs for OTP inputs to prevent re-renders
  const otpRefs = useRef([]);

  useEffect(() => {
    const blockData = JSON.parse(localStorage.getItem("otp_block_info"));
    if (blockData) {
      const { blockTime } = blockData;
      const now = new Date().getTime();
      if (now < blockTime) {
        setIsBlocked(true);
        setBlockTime(blockTime);
      } else {
        localStorage.removeItem("otp_block_info");
      }
    }
  }, []);

  useEffect(() => {
    if (!blockTime) return;
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const diff = blockTime - now;

      if (diff <= 0) {
        setIsBlocked(false);
        setBlockTime(null);
        setTimeLeft(null);
        localStorage.removeItem("otp_block_info");
        localStorage.removeItem("otp_attempts");
        clearInterval(interval);
      } else {
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [blockTime]);

  const handleOtpChange = (value, index) => {
    if (isNaN(value) || value === " ") return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Focus next input
    if (value !== "" && index < 3 && otpRefs.current[index + 1]) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        // Move to previous input if current is empty
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        otpRefs.current[index - 1].focus();
      } else if (otp[index] !== "") {
        // Clear current input
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    if (pastedData.length === 4 && /^\d+$/.test(pastedData)) {
      const pastedDigits = pastedData.split('');
      const newOtp = [...pastedDigits];
      setOtp(newOtp);
      
      // Focus the last input after paste
      setTimeout(() => {
        if (otpRefs.current[3]) {
          otpRefs.current[3].focus();
        }
      }, 10);
    }
  };

  const handleVerify = async () => {
    setError("");
    setMessage("");
    const token = localStorage.getItem("access_token");
    const blockData = JSON.parse(localStorage.getItem("otp_block_info"));
    const now = new Date().getTime();

    if (blockData && now < blockData.blockTime) {
      const remainingMinutes = Math.floor((blockTime - now) / 60000);
      setError(
        `Too many failed attempts. Please try again after ${remainingMinutes} minutes.`
      );
      setTimeLeft(`${remainingMinutes}:00`);
      return;
    } else if (blockData && now >= blockData.blockTime) {
      localStorage.removeItem("otp_block_info");
      localStorage.removeItem("otp_attempts");
    }

    if (!email) {
      setError("Email not found. Please register again.");
      return;
    }

    const otpString = otp.join("");
    if (otpString.length !== 4) {
      setError("Please enter the complete 4-digit OTP code.");
      return;
    }

    if (!token) {
      setError("Session expired. Please register again.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/user/verify-otp/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY,
        },
        body: JSON.stringify({ email, otp: otpString }),
      });
      const data = await response.json();
      if (!response.ok) {
        const currentAttempts =
          parseInt(localStorage.getItem("otp_attempts") || "0", 10) + 1;
        localStorage.setItem("otp_attempts", currentAttempts);
        if (currentAttempts >= 5) {
          const blockTime = now + 60 * 60 * 1000;
          localStorage.setItem("otp_block_info", JSON.stringify({ blockTime }));
          setError("Too many failed attempts. Please try again after 1 hour.");
        } else {
          setError(data.message || data.error || "Verification failed");
        }
      } else {
        localStorage.removeItem("otp_attempts");
        localStorage.removeItem("otp_block_info");

        setMessage("Email verified successfully! Redirecting...");
        setTimeout(() => {
          navigate("/create-profile");
        }, 1500);
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Failed to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setMessage("");
    if (!email) {
      setError("Email not found. Please register again.");
      return;
    }
    
    setResending(true);
    try {
      const response = await fetch(`${BASE_URL}/user/resend-otp/`, {
        method: "POST",
        headers: { "Content-Type": "application/json","x-api-key": process.env.REACT_APP_X_API_KEY},
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || data.error || "Failed to resend OTP");
      } else {
        setMessage("New OTP has been sent to your email.");
        setOtp(["", "", "", ""]); // Clear OTP fields
        // Focus first input
        if (otpRefs.current[0]) {
          otpRefs.current[0].focus();
        }
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Unable to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="grid lg:grid-cols-2">
          {/* Left Column - Illustration */}
          <div className="hidden lg:flex bg-gradient-to-br from-blue-600 to-emerald-600 p-12 flex-col justify-center items-center text-white">
            <div className="w-64 h-64 bg-white/10 rounded-full flex items-center justify-center mb-8">
              <Shield className="w-32 h-32" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Secure Email Verification</h2>
            <p className="text-blue-100 text-center">
              Protect your account with our secure verification system. 
              We've sent a 4-digit code to your email address.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">✓</div>
                <div className="text-sm mt-2">Secure</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">⚡</div>
                <div className="text-sm mt-2">Fast</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">🔒</div>
                <div className="text-sm mt-2">Private</div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="p-8 lg:p-12">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Verify Your Email
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Enter the 4-digit verification code sent to your email
                  </p>
                </div>
              </div>

              {/* Email Display */}
              <div className="bg-blue-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Verification email sent to:</p>
                    <p className="font-medium text-gray-900 break-all">{email || "your@email.com"}</p>
                  </div>
                </div>
              </div>

              {/* OTP Input */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  4-Digit Verification Code
                </label>
                <div className="flex gap-3 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpRefs.current[index] = el}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={handlePaste}
                      onFocus={(e) => e.target.select()}
                      disabled={isBlocked || loading}
                      className="w-16 h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  ))}
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">
                  Enter the 4-digit code from your email
                </p>
              </div>

              {/* Error Messages */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Success Messages */}
              {message && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <p className="text-emerald-700 text-sm">{message}</p>
                  </div>
                </div>
              )}

              {/* Block Timer */}
              {isBlocked && timeLeft && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-yellow-800 font-medium">Account Temporarily Blocked</p>
                      <p className="text-yellow-600 text-sm">
                        You can try again in: <span className="font-bold">{timeLeft}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={handleVerify}
                  disabled={isBlocked || loading || otp.join("").length !== 4}
                  className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    "Verify Email"
                  )}
                </button>

                <div className="flex items-center justify-between">
                  <button
                    onClick={handleResendOtp}
                    disabled={isBlocked || resending}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center gap-2 disabled:opacity-50 transition-colors"
                  >
                    {resending ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Resend OTP
                      </>
                    )}
                  </button>
                  <Link
                    to="/"
                    className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>

              {/* Security Tips */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Security Tips:</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center mt-0.5">✓</div>
                    <span>Check your spam folder if you don't see the email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center mt-0.5">✓</div>
                    <span>Verification code expires in 10 minutes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs flex items-center justify-center mt-0.5">✓</div>
                    <span>Never share your verification code with anyone</span>
                  </li>
                </ul>
              </div>

              {/* Terms */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                  By verifying, you agree to our{" "}
                  <Link to="/terms" className="text-blue-600 hover:text-blue-700 font-medium">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;